import type { PropsWithChildren, ReactNode } from "react";
import { Collapse, Grid, IconButton, Tooltip, Typography } from "@mui/material";
import type { GridProps } from "@mui/material";
import type { SvgIconComponent } from "@mui/icons-material";
import { Authorization } from "@sdeapps/react-core";
import type { ApplicationRole } from "@sdeapps/react-core";
import ContextualMenu from "./Template/ContextualMenu";
import { useContextualMenu } from "hooks";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

interface InformationBoxContentProps extends PropsWithChildren {
  noChildrenPadding?: boolean;
  hasBackground?: boolean;
}

function InformationBoxContent({
  noChildrenPadding = false,
  hasBackground = false,
  children,
}: Readonly<InformationBoxContentProps>): ReactNode {
  return (
    <Grid container size={12} sx={{ flexGrow: 1, mt: 2 }}>
      <Grid
        container
        size={12}
        sx={{
          backgroundColor: hasBackground ? "#f6fafc" : "initial",
          borderRadius: 1,
        }}>
        <Grid container sx={{ p: noChildrenPadding ? 0 : 2 }} spacing={2} size={12}>
          {children}
        </Grid>
      </Grid>
    </Grid>
  );
}

interface InformationBoxContextualMenuProps {
  actions: ReactNode;
  handleClose: VoidFunction;
  handleOpen: (event: React.MouseEvent<HTMLElement>) => void;
  isOpen: boolean;
  menuAnchor: HTMLElement | null;
  closeOnClick: boolean;
}

function InformationBoxContextualMenu({
  actions,
  handleClose,
  handleOpen,
  isOpen,
  menuAnchor,
  closeOnClick,
}: Readonly<InformationBoxContextualMenuProps>): ReactNode {
  return (
    <Grid container sx={{ mr: -2 }} size="auto">
      <IconButton color="inherit" onClick={handleOpen} sx={{ my: -1 }}>
        <Tooltip title="Options" placement="bottom">
          <MoreVertIcon />
        </Tooltip>
      </IconButton>
      <ContextualMenu
        open={isOpen}
        anchorEl={menuAnchor}
        onClose={handleClose}
        closeOnClick={closeOnClick}>
        {actions}
      </ContextualMenu>
    </Grid>
  );
}

interface BaseInformationBoxProps extends GridProps {
  hasBackground?: boolean;
  icon?: SvgIconComponent;
  articleTitle?: ReactNode;
  subtitle?: ReactNode;
  noChildrenPadding?: boolean;
  noHeader?: boolean;
}

interface CollapsibleInformationBoxProps {
  collapsible: true;

  isOpen?: boolean;
  setIsOpen?: (b: boolean) => void;

  actions?: never;
  actionsRoles?: never;
  closeOnClick?: never;
}

interface ActionableInformationBoxProps {
  collapsible?: false;

  actions?: ReactNode;
  actionsRoles?: ApplicationRole | Array<ApplicationRole>;
  closeOnClick?: boolean;

  isOpen?: never;
  setIsOpen?: never;
}

type InformationBoxProps = BaseInformationBoxProps &
  (ActionableInformationBoxProps | CollapsibleInformationBoxProps);

function InformationBox({
  hasBackground = false,
  icon: SvgIcon,
  articleTitle: title,
  subtitle,
  actions,
  actionsRoles,
  children,
  closeOnClick = true,
  noChildrenPadding = false,
  noHeader = false,
  collapsible = false,
  isOpen: isCollapsibleOpen = true,
  setIsOpen: setIsCollapsibleOpen,
}: Readonly<InformationBoxProps>): ReactNode {
  const { isOpen: isContextualMenuOpen, handleOpen, handleClose, menuAnchor } = useContextualMenu();

  return (
    // MAGIE PAS TOUCHER
    <Grid container direction="column" size={12}>
      {!noHeader && (
        <Grid
          container
          size={12}
          sx={{ alignItems: "center", cursor: collapsible ? "pointer" : "auto" }}
          onClick={() => {
            setIsCollapsibleOpen?.(!isCollapsibleOpen);
          }}>
          <Grid
            container
            size={12}
            spacing={0}
            sx={{
              alignItems: "center",
              borderRadius: 1,
              backgroundColor: "primary.main",
              color: "white",
              px: 2,
              py: 1,
            }}>
            <Grid container spacing={1} size="grow" sx={{ alignItems: "center" }}>
              {SvgIcon != null && (
                <Grid container size="auto">
                  <SvgIcon fontSize="small" />
                </Grid>
              )}
              <Typography component={Grid} size="grow" color="inherit" variant="h5" noWrap>
                {title}
              </Typography>
            </Grid>
            <Typography
              component={Grid}
              container
              size="auto"
              color="inherit"
              sx={{ alignItems: "center", fontWeight: "600" }}>
              {subtitle}
            </Typography>
            {!collapsible &&
              actions != null &&
              (actionsRoles != null ? (
                <Authorization roles={actionsRoles}>
                  <InformationBoxContextualMenu
                    actions={actions}
                    handleClose={handleClose}
                    handleOpen={handleOpen}
                    isOpen={isContextualMenuOpen}
                    menuAnchor={menuAnchor}
                    closeOnClick={closeOnClick}
                  />
                </Authorization>
              ) : (
                <InformationBoxContextualMenu
                  actions={actions}
                  handleClose={handleClose}
                  handleOpen={handleOpen}
                  isOpen={isContextualMenuOpen}
                  menuAnchor={menuAnchor}
                  closeOnClick={closeOnClick}
                />
              ))}
            {collapsible && (
              <Grid container size="auto">
                <IconButton color="inherit" sx={{ my: -1, mr: -2, width: "44px", height: "44px" }}>
                  <Tooltip title={isCollapsibleOpen ? "Fermer" : "Ouvrir"} placement="bottom">
                    {isCollapsibleOpen ? (
                      <ExpandLessIcon fontSize="large" />
                    ) : (
                      <ExpandMoreIcon fontSize="large" />
                    )}
                  </Tooltip>
                </IconButton>
              </Grid>
            )}
          </Grid>
        </Grid>
      )}
      {collapsible ? (
        <Collapse in={isCollapsibleOpen} mountOnEnter unmountOnExit>
          <InformationBoxContent
            hasBackground={hasBackground}
            noChildrenPadding={noChildrenPadding}>
            {children}
          </InformationBoxContent>
        </Collapse>
      ) : (
        <InformationBoxContent hasBackground={hasBackground} noChildrenPadding={noChildrenPadding}>
          {children}
        </InformationBoxContent>
      )}
    </Grid>
  );
}

export default InformationBox;
